import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import Tooltip from '@mui/material/Tooltip'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { assignTask, searchAssignableUsers } from 'services/taskService'
import { useSeriesScope } from 'features/taskManagement/useSeriesScope'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { hideEmail } from 'utils/user'

const personName = u =>
  `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name || (u.email ? hideEmail(u.email) : 'Unknown')

const TaskAssigneeQuickEdit = ({
  task,
  bpId,
  onSaved,
  current,
  onSaveIds,
  title = 'Assign people',
  emptyLabel = 'Assign',
  warnIds,
  warnMessage,
}) => {
  const currentPeople = current !== undefined ? current : (task?.assignees || [])
  const currentUser = useSelector(getCurrentUser)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const [ids, setIds] = useState(currentPeople.map(a => a.id))
  const [pool, setPool] = useState([])
  const [pinned, setPinned] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    setIds((current !== undefined ? current : (task?.assignees || [])).map(a => a.id))
  }, [current, task?.assignees])

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const width = 300
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      setCoords({ top: Math.min(r.bottom + 6, window.innerHeight - 360), left })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    let live = true
    setLoading(true)
    const t = setTimeout(() => {
      searchAssignableUsers({ search: search.trim(), buildingPassportId: bpId })
        .then(res => { if (live) setPool(res?.data?.data || []) })
        .catch(() => { if (live) setPool([]) })
        .finally(() => { if (live) setLoading(false) })
    }, 250)
    return () => { live = false; clearTimeout(t) }
  }, [open, search, bpId])

  const people = useMemo(() => {
    const seen = new Set(currentPeople.map(a => a.id))
    const pinnedRows = pinned.filter(u => !seen.has(u.id))
    pinnedRows.forEach(u => seen.add(u.id))
    const merged = [...currentPeople, ...pinnedRows, ...pool.filter(u => !seen.has(u.id))]
    const q = search.trim().toLowerCase()
    if (!q) return merged
    const pinnedIds = new Set(pinned.map(u => u.id))
    return merged.filter(u => pinnedIds.has(u.id) ||
      [personName(u), u.email].filter(Boolean).some(v => String(v).toLowerCase().includes(q))
    )
  }, [currentPeople, pinned, pool, search])

  const toggle = id => setIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const meSelected = currentUser?.id ? ids.includes(currentUser.id) : true
  const addMe = () => {
    if (!currentUser?.id) return
    if (!currentPeople.some(u => u.id === currentUser.id)) {
      setPinned(prev => (prev.some(u => u.id === currentUser.id) ? prev : [...prev, currentUser]))
    }
    setIds(prev => (prev.includes(currentUser.id) ? prev : [...prev, currentUser.id]))
  }

  const warnedNames = useMemo(() => {
    if (!warnIds || warnIds.length === 0) return []
    const warnSet = new Set(warnIds)
    return ids
      .filter(id => warnSet.has(id))
      .map(id => {
        const person = [...currentPeople, ...pool].find(u => u.id === id)
        return person ? personName(person) : null
      })
      .filter(Boolean)
  }, [ids, warnIds, currentPeople, pool])

  const { promptNode, requestScope } = useSeriesScope(task)

  const save = async () => {
    const scope = await requestScope()
    if (scope === null) return
    setBusy(true)
    try {
      const res = await (onSaveIds ? onSaveIds(ids, scope) : assignTask({ taskId: task.id, assigneeIds: ids, scope }))
      const skipped = res?.data?.meta?.series?.skipped
      if (skipped > 0) {
        toast.success(`Saved. ${skipped} upcoming task${skipped === 1 ? '' : 's'} left unchanged. You do not have permission to edit ${skipped === 1 ? 'it' : 'them'}.`)
      } else {
        toast.success('Saved.')
      }
      setOpen(false)
      onSaved?.()
    } catch (e) {
      toast.error('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!task?.id) return null

  const label = currentPeople.length === 0
    ? emptyLabel
    : currentPeople.length <= 2
      ? currentPeople.map(personName).join(', ')
      : `${personName(currentPeople[0])} +${currentPeople.length - 1}`

  const peopleTip = currentPeople.length > 2 ? (
    <PeopleTip>
      {currentPeople.map(p => (
        <span key={p.id}>{personName(p)}</span>
      ))}
    </PeopleTip>
  ) : ''

  return (
    <>
      <Tooltip title={peopleTip} arrow placement='top' disableHoverListener={currentPeople.length <= 2}>
        <Trigger
          ref={btnRef}
          type='button'
          title={currentPeople.length > 2 ? undefined : title}
          $empty={currentPeople.length === 0}
          onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        >
          <GroupOutlinedIcon style={{ fontSize: 14 }} />
          <TriggerText>{label}</TriggerText>
        </Trigger>
      </Tooltip>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label={title} style={{ top: coords.top, left: coords.left }} onClick={e => e.stopPropagation()}>
          <PopLabel>{title}</PopLabel>
          <SearchRow>
            <SearchRoundedIcon style={{ fontSize: 16, color: '#9aa0ab' }} />
            <SearchInput placeholder='Search people…' value={search} autoFocus onChange={e => setSearch(e.target.value)} />
          </SearchRow>
          {!meSelected && (
            <AddMeBtn type='button' onClick={addMe}>
              + Add me{/approver/i.test(title) ? ' as approver' : /assign/i.test(title) ? ' as assignee' : ''}
            </AddMeBtn>
          )}
          <List>
            {people.map(u => {
              const checked = ids.includes(u.id)
              return (
                <RowItem key={u.id} type='button' $checked={checked} onClick={() => toggle(u.id)}>
                  <Tick $on={checked}>{checked ? '✓' : ''}</Tick>
                  <RowText>
                    <RowName>{personName(u)}</RowName>
                    {u.email && <RowMeta>{hideEmail(u.email)}</RowMeta>}
                  </RowText>
                </RowItem>
              )
            })}
            {loading && people.length === 0 && <Empty>Loading…</Empty>}
            {!loading && people.length === 0 && <Empty>No people found.</Empty>}
          </List>
          {warnedNames.length > 0 && (
            <WarnNote role='status'>
              {warnMessage || `${warnedNames.join(', ')} ${warnedNames.length === 1 ? 'is' : 'are'} also assigned to this task. You can still save.`}
            </WarnNote>
          )}
          <PopActions>
            <CancelBtn type='button' onClick={() => setOpen(false)}>Cancel</CancelBtn>
            <SaveBtn type='button' disabled={busy} onClick={save}>{busy ? 'Saving…' : `Save (${ids.length})`}</SaveBtn>
          </PopActions>
        </Pop>,
        document.body
      )}
      {promptNode}
    </>
  )
}

export default TaskAssigneeQuickEdit

const PeopleTip = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px 0;
  max-height: 280px;
  overflow-y: auto;
  font-size: 12.5px;
  white-space: nowrap;
`

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px dashed ${({ $empty, theme }) => ($empty ? theme.colors.grey[500] : 'transparent')};
  background: ${({ $empty }) => ($empty ? '#fff' : '#f2f4f7')};
  color: ${({ $empty, theme }) => ($empty ? theme.colors.grey[800] : theme.colors.haiti)};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover { background: #e8f6f7; border-color: ${({ theme }) => theme.colors.secondary}; }
`
const TriggerText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`
const PopLabel = styled.span`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 9px;
`
const AddMeBtn = styled.button`
  width: 100%;
  margin-top: 7px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  background: #e6f7f9;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.secondary}; color: #fff; }
`
const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font: inherit;
  font-size: 13px;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
`
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  overflow-y: auto;
`
const RowItem = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 7px 8px;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid ${({ $checked }) => ($checked ? '#bfe6ea' : 'transparent')};
  background: ${({ $checked }) => ($checked ? '#f2fbfb' : 'transparent')};
  &:hover { background: ${({ $checked }) => ($checked ? '#eaf8f9' : '#f7f8fa')}; }
`
const Tick = styled.span`
  flex: 0 0 auto;
  width: 19px;
  height: 19px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`
const RowText = styled.div`flex: 1; min-width: 0;`
const RowName = styled.div`font-size: 13px; font-weight: 600; color: #1c2029; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const RowMeta = styled.div`font-size: 11px; color: #8a90a0;`
const Empty = styled.div`font-size: 12.5px; color: #8a90a0; padding: 10px 4px;`
const WarnNote = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: #d97706;
  background: #fef3c7;
  border-radius: 8px;
  padding: 7px 10px;
`
const PopActions = styled.div`display: flex; justify-content: flex-end; gap: 8px;`
const CancelBtn = styled.button`
  padding: 7px 13px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`
const SaveBtn = styled.button`
  padding: 7px 15px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
